/**
 * The extension's page adapters (#195) — pure functions over a Document.
 *
 * The extension RENDERS and COLLECTS; only Voyager concludes. Nothing here
 * decides what a number means: an adapter finds tables and labelled numbers
 * on the page, the server grades them. A page with nothing to collect is a
 * page the extension stays silent on.
 *
 * Lives in ui-app so the app's jest (jsdom) covers it; the extension bundle
 * imports it.
 */

import { EXTENSION_VERDICT_COLORS, EXTENSION_VERDICT_COLORS_DARK } from '../design/tokens';

export type PageKind = 'tableau' | 'email' | 'calendar' | 'generic';

export interface CollectedTable {
  headers: string[];
  rows: string[][];
  /** the cell element behind each data cell, for the verdict chip */
  cells: (Element | null)[][];
  element: Element;
}

export interface CollectedInline {
  id: string;
  label: string;
  rendered: string;
  context: string;
  node: Text;
  offset: number;
  comparison?: 'wow' | 'yoy';
}

export interface DateWindow { start: string; end: string }

export const MAX_TABLES = 20;
export const MAX_ROWS = 200;
export const MAX_COLS = 24;
export const MAX_INLINE = 120;
const CELL_MAX = 200;

const EMAIL_HOSTS = ['mail.google.com', 'outlook.office.com', 'outlook.office365.com', 'outlook.live.com', 'mail.yahoo.com', 'mail.proton.me'];
// a calendar is never read for numbers
const CALENDAR_HOSTS = ['calendar.google.com'];

export function tableauView(url: string): { server: string; host: string; site: string; workbook: string; view: string } | null {
  let u: URL;
  try { u = new URL(url); } catch { return null; }
  const raw = u.hash && u.hash.includes('/views/') ? u.hash : u.pathname;
  let path: string;
  try { path = decodeURIComponent(raw); } catch { path = raw; }   // a broken percent escape is still a URL, read as written
  const m = /\/views\/([^/?#]+)\/([^/?#]+)/.exec(path);
  if (!m) return null;
  const site = /\/site\/([^/]+)\//.exec(path);
  return { server: `${u.protocol}//${u.host}`, host: u.host.toLowerCase(), site: site ? site[1] : '', workbook: m[1], view: m[2] };
}

/**
 * What kind of page this is — decides which adapter reads it and whether it
 * is read at all before the panel opens. Only a Tableau view URL or a known
 * mail host is read on load; a page is never taken for a mailbox from its
 * markup alone (a `.a3s` or a `role="document"` on some other site is that
 * site's business), so nothing leaves an unknown site unless someone opens
 * Voyager on it.
 */
export function pageKind(url: string, _doc: Document): PageKind {
  if (tableauView(url)) return 'tableau';
  let host = '';
  try { host = new URL(url).host.toLowerCase(); } catch { host = ''; }
  if (EMAIL_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))) return 'email';
  if (CALENDAR_HOSTS.some((h) => host === h)) return 'calendar';
  return 'generic';
}

/** Is this element shown — not hidden itself or by an ancestor? (Gmail collapses read messages with display:none;
 *  quoted history and alternate tables hide inside a message.) `cache` makes a whole page's walk cheap. */
export function isShown(el: Element, cache?: WeakMap<Element, boolean>): boolean {
  const known = cache?.get(el);
  if (known !== undefined) return known;
  let shown = true;
  const inline = (el as HTMLElement).style;
  if (el.hasAttribute('hidden') || el.getAttribute('aria-hidden') === 'true') shown = false;
  else if (inline && (inline.display === 'none' || inline.visibility === 'hidden')) shown = false;
  else {
    // a document without a window (one built in memory) has no computed style: the inline style above is what there is
    const view = el.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : null);
    let cs: CSSStyleDeclaration | null = null;
    try { cs = view ? view.getComputedStyle(el) : null; } catch { cs = null; }
    if (cs && (cs.display === 'none' || cs.visibility === 'hidden')) shown = false;
    else if (el.parentElement && el !== el.ownerDocument.body) shown = isShown(el.parentElement, cache);
  }
  cache?.set(el, shown);
  return shown;
}

/**
 * The message body on a mail page — the newest OPEN message, and only that:
 * a mailbox with no message open has nothing to ground, and its inbox rows
 * and snippets are never read. Null when there is no open message. On any
 * other page, the page.
 */
export function messageRoot(doc: Document, kind: PageKind, url = ''): Element | null {
  const roots = messageRoots(doc, kind, url);
  return roots.length ? roots[roots.length - 1] : null;   // the open / most-recent message, for sender & subject
}

/** EVERY message body the reader can see. Gmail renders one `.a3s` per message in a thread, so reading only the
 *  last one misses the numbers (a scorecard, a table) that live in an earlier, still-expanded message. A
 *  collapsed / hidden message is not what the reader sees and is left out. */
export function messageRoots(doc: Document, kind: PageKind, url = ''): Element[] {
  if (kind === 'email') {
    let host = '';
    try { host = new URL(url).host.toLowerCase(); } catch { host = ''; }
    const gmailHost = host === 'mail.google.com' || host.endsWith('.mail.google.com');
    // The .a3s fan-out is GMAIL-specific (there .a3s is one message body per message). Only run it on Gmail's
    // host — a coincidental .a3s on any other site never triggers a multi-body read. (No url = an in-memory doc
    // in a test; allow it there.)
    if (gmailHost || !url) {
      const gmail = Array.from(doc.querySelectorAll('.a3s')).filter((el) => isShown(el) && notEditor(el));   // .a3s is a message body: shallow — received textboxes are pruned in collection, not rejected
      if (gmail.length) return topmost(gmail);
    }
    // Everything else (Outlook and the rest): [role="document"] is generic (reading-pane body, but also compose
    // surfaces and side panes), so read only the ONE open message body — the last shown, non-editor one.
    const outlook = Array.from(doc.querySelectorAll('[role="document"]')).filter((el) => isShown(el) && notEditor(el, true));   // generic: reject a compose wrapper around a textbox
    return outlook.length ? [outlook[outlook.length - 1]] : [];
  }
  const body = doc.body || doc.documentElement;
  return body ? [body] : [];
}

/** Drop any element contained in another in the list, so nested message nodes are not collected twice. */
function topmost(els: Element[]): Element[] {
  return els.filter((e) => !els.some((o) => o !== e && o.contains(e)));
}

// One selector for "an editor" — contenteditable (any value but false) or any ARIA textbox role (role may hold
// several space-separated tokens, so ~= not =).
const EDITOR_CSS = '[contenteditable]:not([contenteditable="false"]), [role~="textbox"]';

/** Text under `el` with editor subtrees excluded (nothing being drafted), and — when `shown` is given — hidden
 *  subtrees too. Bounded. Adjacent RETAINED runs keep their adjacency (a number split across inline elements
 *  stays one number); a run separated from the next by a PRUNED subtree gets a single space boundary, so an
 *  editor between "17" and ",516" cannot merge them into a fabricated "17,516". */
// A superscript whose whole text is a reference mark — "1", "2", "*", "†" — points AT a footnote; it is
// not part of the name it trails. Three things separate a reference from content that happens to be raised,
// and a marker has to pass all of them, because pruning real data is the worse failure:
//   · it is a SUPERSCRIPT — a subscript is chemistry or maths ("CO₂" is a name, not a citation);
//   · it is not its host's WHOLE text — a cell reading only "924" superscripted is the value itself;
//   · it trails a WORD, not a symbol — "m³" and "km²" ride on a unit, so a short token before the mark
//     means an exponent.
const FOOTNOTE_MARK = /^\s*(?:\d{1,2}|[*†‡§¶#]{1,2})\s*$/;
const UNIT_TOKEN = /([A-Za-z]+)\s*$/;
function isFootnoteMark(e: Element): boolean {
  if (e.tagName !== 'SUP') return false;
  const mark = (e.textContent || '').trim();
  if (!FOOTNOTE_MARK.test(mark)) return false;
  if (((e.parentElement?.textContent) || '').trim() === mark) return false;
  const word = UNIT_TOKEN.exec((e.previousSibling?.textContent) || '');
  return !!word && word[1].length >= 3;
}

export function textSansEditors(el: Element, cap = 100000, shown?: WeakMap<Element, boolean>,
                                opts: { dropFootnotes?: boolean } = {}): string {
  const doc = el.ownerDocument || document;
  let pendingBoundary = false;
  const walker = doc.createTreeWalker(el, 1 | 4 /* ELEMENT | TEXT */, {
    acceptNode(n: Node): number {
      const e = n.nodeType === 1 ? (n as Element) : (n as Text).parentElement;
      if (!e) return 1;
      // A footnote MARKER is not part of the name it trails, and it is pruned like any other content the
      // name does not include — with a boundary, so "New users<sup>1</sup>" reads "New users" and never
      // the run-together "New users1": a term no catalog metric can answer to, and the name a captured
      // definition would be filed under for everyone after.
      if (opts.dropFootnotes) {
        const marker = e.closest('sup, sub');
        if (marker && isFootnoteMark(marker)) { pendingBoundary = true; return 2 /* FILTER_REJECT */; }
      }
      // OUR OWN chip is transparent: skip it WITHOUT a boundary, so the extraction — and the fingerprint built
      // from it — is identical whether or not our chips are on the page. (A chip that added a boundary made the
      // fingerprint flip as chips came and went, which re-grounded in a loop: the highlight flickered.)
      const chip = e.closest(`.${CHIP_CLASS}`);
      if (chip && isOwned(chip)) return 2 /* FILTER_REJECT, no boundary */;
      // real page content that is pruned (an editor, a hidden subtree) DOES separate the runs around it
      if (e.closest(EDITOR_CSS) || (!!shown && !isShown(e, shown))) { pendingBoundary = true; return 2; }
      return n.nodeType === 1 ? 3 /* FILTER_SKIP: descend, don't emit the element */ : 1 /* FILTER_ACCEPT */;
    },
  });
  let out = '';
  let node = walker.nextNode();
  while (node && out.length < cap) {
    if (out && pendingBoundary) out += ' ';
    pendingBoundary = false;
    out += (node as Text).data;
    node = walker.nextNode();
  }
  return out.slice(0, cap);
}

// A message being DRAFTED is never read. `deep` also rejects a root that WRAPS an editor — used only for
// Outlook's generic [role="document"] (a compose surface holding a [role="textbox"]); NEVER for Gmail's
// message-specific .a3s, or a sender could embed a [role="textbox"] in a received message to suppress grounding.
const EDITOR_SEL = '[contenteditable]:not([contenteditable="false"]), [role="textbox"]';   // '', true, plaintext-only, events — anything but false
function notEditor(el: Element, deep = false): boolean {
  if (el instanceof HTMLElement && el.isContentEditable) return false;
  if (el.closest(EDITOR_CSS)) return false;
  return !(deep && el.querySelector(EDITOR_CSS));
}

/** A cell worth grading: a number as a report prints one — not a year, a date, a time or a version. */
export function looksNumeric(text: string): boolean {
  const t = (text || '').trim();
  if (!t || STOP.test(t)) return false;
  return /^(?:[$€£]\s?)?[-+−]?\d[\d,]*(?:\.\d+)?\s?(?:%|pts?|pp|min|[kKMB])?$/.test(t) || /\d[\d,]*(?:\.\d+)?\s?(?:%|pts?|min)\b/.test(t);
}

const clean = (s: string | null | undefined) => (s || '').replace(/\s+/g, ' ').trim().slice(0, CELL_MAX);
const prose = (s: string | null | undefined, max = 4000) => (s || '').replace(/\s+/g, ' ').trim().slice(0, max);

/** The logical grid of a table's rows: `rowspan`/`colspan` expanded, so each slot holds the element that covers it. */
function gridOf(trs: Element[], shown: WeakMap<Element, boolean>): Element[][] {
  const grid: Element[][] = [];
  const carry = new Map<number, { cell: Element; rows: number }>();      // column → the cell spanning down into the next rows
  for (const tr of trs) {
    const row: Element[] = [];
    const cells = Array.from(tr.children).filter((c) => (c.tagName === 'TD' || c.tagName === 'TH') && isShown(c, shown));
    let col = 0;
    let next = 0;
    const lastCarried = () => (carry.size ? Math.max(...Array.from(carry.keys())) : -1);
    // a ragged row: its own cells may end before a column a cell above still spans — keep walking to it
    while ((next < cells.length || col <= lastCarried()) && col < MAX_COLS) {
      const above = carry.get(col);
      if (above) {
        row[col] = above.cell;
        if (--above.rows <= 0) carry.delete(col); else carry.set(col, above);
        col += 1;
        continue;
      }
      if (next >= cells.length) { col += 1; continue; }   // a gap before a carried column
      const cell = cells[next++];
      const colspan = Math.max(1, Math.min(MAX_COLS, parseInt(cell.getAttribute('colspan') || '1', 10) || 1));
      const rowspan = Math.max(1, Math.min(MAX_ROWS, parseInt(cell.getAttribute('rowspan') || '1', 10) || 1));
      for (let k = 0; k < colspan && col < MAX_COLS; k += 1, col += 1) {
        row[col] = cell;
        if (rowspan > 1) carry.set(col, { cell, rows: rowspan - 1 });
      }
    }
    grid.push(row);
  }
  return grid;
}

/** Every data table under `root`: a header row (th, or the first row) and at least one data row; layout tables are skipped. */
// Inside a table a footnote marker is never data: not in a row's name, not in a header, not beside a
// value. Every cell is read without one.
const FOOTNOTES = { dropFootnotes: true } as const;

export function collectTables(root: Element, max = MAX_TABLES, shown: WeakMap<Element, boolean> = new WeakMap()): CollectedTable[] {
  const out: CollectedTable[] = [];
  const tables = Array.from(root.querySelectorAll('table'));
  for (const table of tables) {
    if (out.length >= max) break;
    if (!isShown(table, shown)) continue;                       // hidden: not what the reader sees
    if (table.querySelector('table')) continue;                 // a layout table holding other tables
    if (table.closest(EDITOR_CSS)) continue;   // a table inside an editor is being drafted, not read
    const trs = Array.from(table.querySelectorAll('tr')).filter((tr) => tr.closest('table') === table && isShown(tr, shown));   // a hidden row is not what the reader sees
    if (trs.length < 2) continue;
    // the table as a GRID: a cell spanning rows or columns occupies every slot it covers, so a row after a
    // row-spanning label still has its label first and its values in their columns; a hidden cell is not
    // what the reader sees and is left out
    const rowsCells = gridOf(trs, shown);
    const widths = rowsCells.map((r) => r.length);
    if (Math.max(...widths) < 2) continue;                        // one column: not a table of numbers
    // A COLUMN-header row has a <th> in a VALUE column (col ≥ 1). A `<th scope="row">` (or a bare <th>) in
    // the label column alone is a ROW header — an accessible label, not a declaration of column names — so
    // a table of "<th>Revenue</th><td>$1.2M</td>" rows is NOT treated as having a header row.
    const isHeaderRow = (r: Element[]) => r.length >= 2 && r.slice(1).some((c) => c && c.tagName === 'TH');
    const firstWide = rowsCells.findIndex((r) => r.length >= 2);
    if (firstWide < 0) continue;
    const cellText = (c: Element | null) => (c ? clean(textSansEditors(c, CELL_MAX, shown, FOOTNOTES)) : '');
    // A cell's NUMERIC lead — the number before a trailing annotation, the way the backend reads it
    // ("17,516 (current week)" → "17,516", "7,512 (-2.1%)" → "7,512"). So an annotated value cell still
    // reads as a number for header/data detection and column naming.
    const lead = (t: string) => t.replace(/\s*\(.*\)\s*$/, '').trim();
    const numericCell = (t: string) => looksNumeric(t) || looksNumeric(lead(t));
    // For header-vs-data detection: a cell that OPENS with a number — even one carrying a trailing marker
    // ("-12.7% WoW", "+500 (WoW)") — is a value/delta, not a column NAME. A column name is a label word
    // ("This week", "Metric") that does not begin with a figure. So a data row whose value is a suffixed
    // percentage is not mistaken for a header.
    // Opens with a report figure — but NOT a date, a date range or a 19xx/20xx year ("2026-08-17 to
    // 2026-08-23", "2026"), which is a column LABEL, not a value. Keeps a dated column heading on the
    // header side of the header-vs-data split.
    const DATEISH = /^[-+−]?\s?(?:\d{4}[-/]\d{1,2}[-/]\d{1,2}|\d{1,2}[-/]\d{1,2}[-/]\d{2,4}|(?:19|20)\d{2}(?:\D|$)|\d{1,2}:\d{2})/;
    const numberish = (t: string) => { const s = t.trim(); return /^[-+−]?\s?[$€£]?\s?\d/.test(s) && !DATEISH.test(s); };
    // A headerless scorecard ("Orders | 17,516 | -12.7% WoW", no <th>) is DATA, not a header: consuming
    // its first row as column names turns "17,516" into a label. Tell a header row from a data row without
    // guessing on the row alone — a row DECLARES column names when it has a <th>, or its label cell is a
    // pure column-header word (Metric/KPI/Measure — never a metric's own name), or one of its value cells
    // is non-numeric TEXT sitting over a column that is numeric below it ("This week" above the numbers).
    // Absent all three, a first row that itself carries a number is data, and every wide row is kept.
    const row0 = rowsCells[firstWide];
    const below = rowsCells.slice(firstWide + 1);
    // Whether a column is numeric below row0 (the majority of its cells). Used only to spot a TEXT header
    // sitting over numbers — never to compare one number kind with another: a scorecard's single value
    // column legitimately mixes units down its rows (Conversion rate 4.2%, Revenue $1.2M), so a
    // percent-over-number difference is NOT evidence of a header.
    const numericBelow = (j: number) => {
      const vals = below.map((r) => cellText(r[j])).filter(Boolean);
      return vals.length > 0 && vals.filter(numberish).length * 2 >= vals.length;
    };
    const declaresHeader = rowsCells.some(isHeaderRow)
      // a pure column-header / dimension word in the label cell — never a metric's own name
      || /^(?:metric|metrics|kpi|kpis|measure|measures|percentile|segment|category|dimension|name|item|type|group|cohort|region|store|channel|market|day|days|date|week|month|quarter|year|period|hour|bucket|rank)$/i.test(cellText(row0[0]))
      // a value cell that is non-numeric TEXT sitting over a numeric column: it names the column ("This
      // week" above the numbers), it is not a value
      || row0.slice(1).some((c, k) => { const t = cellText(c); return !!t && !numberish(t) && numericBelow(k + 1); });
    const headerless = !declaresHeader && row0.slice(1).some((c) => numberish(cellText(c)));
    const headerIdx = rowsCells.findIndex(isHeaderRow) >= 0 ? rowsCells.findIndex(isHeaderRow) : firstWide;
    // a header may be more than one row (a group above its columns): every leading row of header cells
    // is part of it, a column's name the distinct words down its slots — "Orders · This week"
    let headerEnd = headerIdx;
    while (!headerless && headerEnd + 1 < rowsCells.length && rowsCells[headerEnd + 1].length >= 2 && rowsCells[headerEnd + 1].every((c) => !c || c.tagName === 'TH')) headerEnd += 1;
    const dataCells = rowsCells.slice(headerless ? firstWide : headerEnd + 1, (headerless ? firstWide : headerEnd + 1) + MAX_ROWS);
    const headers: string[] = [];
    if (headerless) {
      const width = Math.max(...dataCells.map((r) => r.length), 0);
      const colTexts = Array.from({ length: width }, (_, col) => dataCells.map((r) => cellText(r[col])).join(' '));
      const named: string[] = new Array(width).fill('');
      // A column holds an ABSOLUTE number when some cell is numeric and not a bare percent ("17,516",
      // "$831.8K", "3.56"). Such a column is a VALUE, never a comparison — so a column mixing absolute
      // values with one delta cell ("17,516" … "-12.7% WoW") is not turned into a WoW column by that one
      // cell, which would grade the absolute values as week-over-week changes.
      const hasAbsolute = (col: number) => dataCells.some((r) => { const t = lead(cellText(r[col])); return looksNumeric(t) && !t.includes('%'); });
      // The part of a cell AFTER its lead number — the annotation ("15,000 (previous week)" → "(previous
      // week)", "120,000 (+20% vs last week)" → "(+20% vs last week)").
      const annot = (t: string) => t.slice(lead(t).length);
      // A period the cells name themselves ("15,000 (previous week)", "17,516 this week"). Prior is claimed
      // on a plain period label, but NOT when the phrase is a comparison BASELINE ("vs last week") — there
      // the lead is a current value and the period names what it is compared against. A "last week" label
      // that merely sits beside a redundant WoW delta ("95,000 (last week; +5% WoW)") is still the prior
      // column. Mirrors the backend vocabulary and its wow-before-prior precedence for the "vs" form.
      // A comparison BASELINE: the period word is connected to the delta by a preposition ("+20% from last
      // week", "vs last week", "compared with last week") — the lead is current and the period names what it
      // is measured against. A standalone "(last week)" with no such connector is a prior-level label.
      const vsBaseline = (col: number) => /(?:vs\.?|versus|compared\s+(?:with|to)|against|relative\s+to|from|than)\s+(?:the\s+)?(?:last|prior|previous)\b/i.test(colTexts[col]);
      // A prior-period label describes the LEAD only when the annotation is a pure label — not when it
      // SUPPLIES a separate absolute value ("120,000 (last week: 100,000)": 120,000 is current, 100,000 is
      // last week's). After stripping deltas (signed numbers / percents) and money, any bare number left in
      // an annotation means it carries its own value, so the lead is current, not prior.
      const annotSuppliesValue = (col: number) => dataCells.some((r) => {
        // strip only SIGNED-percent deltas ("+5%", "-2%"); anything left with a digit — a plain or monetary
        // number ("100,000", "-$5,000") OR an unsigned rate ("4.0%") — is a supplied period VALUE, so the
        // lead is the current figure, not a prior level
        const a = annot(cellText(r[col])).replace(/[-+−]\s?\d[\d,]*(?:\.\d+)?\s?%/g, '');
        return /\d/.test(a);
      });
      // Prior is claimed only with an explicit period UNIT (week / year) — a bare "prior"/"previous" could be
      // a month or a quarter, which the backend has no role for, so it stays unclaimed rather than graded as
      // a prior WEEK. "week-over-week" is a comparison marker, handled below, not a prior-level label.
      const periodOf = (col: number): '' | 'prior_yoy' | 'prior_wow' | 'value' => {
        const t = colTexts[col].toLowerCase();
        if (!vsBaseline(col) && !annotSuppliesValue(col)) {
          if (/\b(?:last|prior|previous)\s+year\b|\byear\s+ago\b/.test(t)) return 'prior_yoy';
          if (/\b(?:last|prior|previous)\s+week\b|\bweek\s+ago\b/.test(t)) return 'prior_wow';
        }
        if (/\b(?:this week|current|actual|latest|this period)\b/.test(t)) return 'value';
        return '';
      };
      // The share of a column's non-empty cells that carry a marker — a comparison column is HOMOGENEOUS
      // (every cell is a WoW), so a lone delta cell among absolute values does not flip the column, while a
      // marked column ("+500 (WoW)") is a comparison even though its number parses absolute.
      const markerShare = (col: number, re: RegExp) => {
        const cs = dataCells.map((r) => cellText(r[col])).filter(Boolean);
        return cs.length ? cs.filter((t) => re.test(t)).length / cs.length : 0;
      };
      // The marker labels the LEAD itself when no cell has a separate number in its annotation ("+500
      // (WoW)", "-12.7% WoW" → the lead is the change). When the annotation carries its own number
      // ("4.2% (+0.3 pp WoW)", "-250,000 (+10% WoW)") the lead is a VALUE and the annotation is the change,
      // so the column is not a comparison.
      const leadIsDelta = (col: number) => {
        const cs = dataCells.map((r) => cellText(r[col])).filter(Boolean);
        return cs.length > 0 && cs.every((t) => !/\d/.test(annot(t)));
      };
      // A comparison column carries ONE kind of marker across its cells. The backend grades a whole column
      // by one header role, so a column mixing WoW and YoY rows ("+6% (WoW)" … "+18% (YoY)") cannot be
      // represented — naming it either would mis-grade the other rows. Such a mixed column is left unnamed
      // (its deltas simply do not ground) rather than graded wrong.
      const nameComparison = (col: number) => {
        const wow = markerShare(col, /\b(?:wow|w\/w|week[- ]over[- ]week)\b/i);
        const yoy = markerShare(col, /\b(?:yoy|y\/y|year[- ]over[- ]year)\b/i);
        const plan = markerShare(col, /\bvs\.?\s*plan\b/i);
        if ([wow > 0, yoy > 0, plan > 0].filter(Boolean).length !== 1) return false;   // none or mixed
        if (wow >= 0.5) { named[col] = 'WoW'; return true; }
        if (yoy >= 0.5) { named[col] = 'YoY'; return true; }
        if (plan >= 0.5) { named[col] = 'vs plan'; return true; }
        return false;
      };
      // Name each column by the period its cells declare, then by comparison markers. This runs for EVERY
      // numeric column (a rate scorecard's value columns are percents, not absolutes), so "prior week" vs
      // "current week" is honoured on percent columns too. A column is graded as a CHANGE only when its
      // marker labels the lead directly ("+500 (WoW)", "-12.7% WoW"); a value whose annotation carries the
      // change ("4.2% (+0.3 pp WoW)", "17,516 (current week; +4.2% WoW)") keeps the value/prior role.
      for (let col = 1; col < width; col += 1) {
        const p = periodOf(col);
        if (p === 'prior_yoy') { named[col] = 'Last year'; continue; }       // header role: prior_yoy
        if (p === 'prior_wow') { named[col] = 'Prior week'; continue; }      // header role: prior_wow
        if (p === 'value') { named[col] = 'This week'; continue; }
        if (leadIsDelta(col)) nameComparison(col);                          // else a value/level: chosen below
      }
      // The VALUE ("This week"): a column the cells marked current already took it; else the first unnamed
      // numeric column, an absolute level preferred over a bare percent, so a marker-less percent ("-12.7%")
      // beside a real value is left unnamed (dropped, never mistaken for the value). A rate scorecard whose
      // only numeric columns are percents still names its first one the value.
      if (!named.some((h) => h === 'This week')) {
        let valueCol = -1;
        for (let col = 1; col < width; col += 1) if (!named[col] && hasAbsolute(col)) { valueCol = col; break; }
        if (valueCol < 0) for (let col = 1; col < width; col += 1) if (!named[col] && numberish(colTexts[col])) { valueCol = col; break; }
        if (valueCol >= 0) named[valueCol] = 'This week';
      }
      headers.push(...named);
      headers[0] = '';
      if (!headers.some(Boolean)) continue;                          // no value column to name: nothing to ground
    } else {
      const width = Math.max(...rowsCells.slice(headerIdx, headerEnd + 1).map((r) => r.length));
      for (let col = 0; col < width; col += 1) {
        const words: string[] = [];
        for (let hr = headerIdx; hr <= headerEnd; hr += 1) {
          const cell = rowsCells[hr][col]; const text = cell ? clean(textSansEditors(cell, CELL_MAX, shown, FOOTNOTES)) : '';
          if (text && !words.includes(text)) words.push(text);
        }
        headers.push(words.join(' · '));
      }
      if (headers.filter(Boolean).length < 2) continue;
    }
    const rows: string[][] = [];
    const cells: (Element | null)[][] = [];
    for (const r of dataCells) {
      const texts = r.map((c) => (c ? clean(textSansEditors(c, CELL_MAX, shown, FOOTNOTES)) : ''));
      if (!texts.some(Boolean)) continue;
      rows.push(texts);
      cells.push(r.map((c) => c));
    }
    if (!rows.length || !rows.some((r) => r.slice(1).some(numericCell))) continue;   // no numbers: nothing to ground
    out.push({ headers, rows, cells, element: table });
  }
  return out;
}

// a number worth asking about: thousands with separators, money, a percentage or a unit, or a decimal —
// never a bare small integer ("12 new zones", "page 3") or a bare thousands suffix ("per 1k orders")
const NUMBER = /(?:[$€£]\s?)?[-+−]?\d{1,3}(?:,\d{3})+(?:\.\d+)?(?:\s?(?:%|pts?\b|pp\b|min\b))?|(?:[$€£]\s?)[-+−]?\d+(?:\.\d+)?(?:\s?[kKMB]\b)?|[-+−]?\d+(?:\.\d+)?\s?(?:%|pts?\b|pp\b|min\b)|[-+−]?\d+\.\d+\b/g;
const STOP = /\b(?:19|20)\d{2}\b|\d{1,2}:\d{2}|\b\d{1,2}\/\d{1,2}\b|\bpage \d|\bstep \d|\bv?\d+\.\d+\.\d+\b/;
const LABEL_STOP = new Set(['the', 'a', 'an', 'of', 'and', 'or', 'to', 'in', 'on', 'at', 'for', 'with', 'by', 'from', 'vs', 'is', 'was', 'were', 'are']);

function labelWords(words: string[], fromEnd: boolean): string {
  const picked: string[] = [];
  const seq = fromEnd ? [...words].reverse() : words;
  for (const w of seq) {
    if (/^[\d$€£+−-]/.test(w)) break;                       // another number: the label stops here
    const ends = /[,;.!?)]$/.test(w);                          // a clause boundary
    if (fromEnd && ends && picked.length) break;               // reading backwards: the previous clause is not this number's label
    const t = w.replace(/^[^A-Za-z]+|[^A-Za-z]+$/g, '');
    if (!t) break;
    picked.push(t);
    if (picked.length >= 4 || (!fromEnd && ends)) break;      // reading forwards: the label ends with the clause
  }
  const arr = fromEnd ? picked.reverse() : picked;
  while (arr.length && LABEL_STOP.has(arr[arr.length - 1].toLowerCase())) arr.pop();
  while (arr.length && LABEL_STOP.has(arr[0].toLowerCase())) arr.shift();
  return arr.join(' ');
}

/** Labelled numbers in prose (outside tables): "Support tickets: 1,135", "1,135 tickets opened (+10.7% WoW)". Low confidence by construction. */
export function collectInline(root: Element, max = MAX_INLINE, shown: WeakMap<Element, boolean> = new WeakMap()): CollectedInline[] {
  const out: CollectedInline[] = [];
  const doc = root.ownerDocument || document;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let n = 0;
  let node = walker.nextNode() as Text | null;
  while (node && out.length < max) {
    const text = node.data;
    const parent = node.parentElement;
    if (!parent || (parent.closest('table, script, style, noscript, textarea, input') || parent.closest(EDITOR_CSS)) || !isShown(parent, shown)) { node = walker.nextNode() as Text | null; continue; }
    if (text && text.length >= 3 && /\d/.test(text)) {
      NUMBER.lastIndex = 0;
      let m: RegExpExecArray | null;
      while ((m = NUMBER.exec(text)) && out.length < max) {
        const rendered = m[0].trim();
        const before = text.slice(Math.max(0, m.index - 80), m.index);
        const after = text.slice(m.index + rendered.length, m.index + rendered.length + 80);
        if (STOP.test(before.slice(-12) + rendered + after.slice(0, 12))) continue;
        const afterWords = after.replace(/^[\s:—–-]+/, '').split(/\s+/);
        const beforeWords = before.replace(/[\s:—–(\[-]+$/, '').split(/\s+/);
        // "1,135 tickets opened" → the words after; "Support tickets: 1,135" → the words before;
        // "$1,234,567 this week (+4.2% WoW)" → the change's label is the words before it, not the comparison word after;
        // "zone-hours 9.7% of all zone-hours" → a number followed by "of …" is labelled by what precedes it
        const afterLabel = labelWords(afterWords, false);
        const beforeLabel = labelWords(beforeWords, true);
        const afterIsWeak = /[:—–-]\s*$/.test(before) || /^(wow|yoy|w\/w|y\/y|vs|week|year)\b/i.test(afterLabel)
          || LABEL_STOP.has((afterWords[0] || '').toLowerCase()) || /^[)\].,;]/.test(after.trim());
        const label = afterIsWeak ? (beforeLabel || afterLabel) : (afterLabel || beforeLabel);
        if (!label || label.length < 3) continue;
        const context = clean(before.slice(-60) + rendered + after.slice(0, 60));
        const cmp = /\b(wow|week[- ]over[- ]week)\b/i.test(after.slice(0, 40)) ? 'wow' : /\b(yoy|year[- ]over[- ]year)\b/i.test(after.slice(0, 40)) ? 'yoy' : undefined;
        n += 1;
        out.push({ id: `i${n}`, label, rendered, context, node, offset: m.index, ...(cmp && /%|pts?$/.test(rendered) ? { comparison: cmp } : {}) });
      }
    }
    node = walker.nextNode() as Text | null;
  }
  return out;
}

const MONTHS: Record<string, number> = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, sept: 9, oct: 10, nov: 11, dec: 12 };
const iso = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

/** The week the page is about, from its own words: "Week of Aug 24–30, 2026", "week of Aug 24-30", "2026-08-24 to 2026-08-30". */
export function windowHint(text: string, today: Date = new Date()): DateWindow | null {
  const t = (text || '').replace(/\s+/g, ' ');
  let m = /(\d{4})-(\d{2})-(\d{2})\s*(?:to|–|-|→)\s*(\d{4})-(\d{2})-(\d{2})/.exec(t);
  if (m) {
    // an ISO range is checked like a worded one: a date that is not on the calendar, or a range backwards, is no window
    const [y1, mo1, d1, y2, mo2, d2] = m.slice(1).map(Number);
    if (!validDay(y1, mo1, d1) || !validDay(y2, mo2, d2)) return null;
    const start = iso(y1, mo1, d1);
    const end = iso(y2, mo2, d2);
    return start <= end ? { start, end } : null;
  }
  m = /week of ([A-Za-z]{3,9})\.? (\d{1,2})\s*(?:–|-|to)\s*(?:([A-Za-z]{3,9})\.? )?(\d{1,2})(?:,? (\d{4}))?/i.exec(t)
    || /\b([A-Za-z]{3,9})\.? (\d{1,2})\s*(?:–|-)\s*(?:([A-Za-z]{3,9})\.? )?(\d{1,2})(?:,? (\d{4}))?\b/.exec(t);
  if (!m) return null;
  const m1 = MONTHS[m[1].slice(0, 4).toLowerCase()] || MONTHS[m[1].slice(0, 3).toLowerCase()];
  const m2 = m[3] ? (MONTHS[m[3].slice(0, 4).toLowerCase()] || MONTHS[m[3].slice(0, 3).toLowerCase()]) : m1;
  if (!m1 || !m2) return null;
  const d1 = Number(m[2]);
  const d2 = Number(m[4]);
  // a year written after the range belongs to its END ("Dec 29–Jan 4, 2026" ends in 2026 and began in 2025);
  // with none written, the range begins this year and a wrap runs into the next
  let y1: number;
  let y2: number;
  if (m[5]) { y2 = Number(m[5]); y1 = m2 < m1 ? y2 - 1 : y2; } else { y1 = today.getFullYear(); y2 = m2 < m1 ? y1 + 1 : y1; }
  if (!validDay(y1, m1, d1) || !validDay(y2, m2, d2)) return null;      // "Feb 29–30" is not a week
  const start = iso(y1, m1, d1);
  const end = iso(y2, m2, d2);
  return start <= end ? { start, end } : null;
}

function validDay(y: number, m: number, d: number): boolean {
  if (d < 1 || d > 31) return false;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
}

export const CHIP_CLASS = 'voyager-chip';
export const MARK_CLASS = 'voyager-mark';

// Ownership is node identity, not a class name: a page element that happens to
// carry our class is never unwrapped, removed, or treated as ours.
const OWNED = new WeakSet<Element>();
export function own<T extends Element>(el: T): T { OWNED.add(el); return el; }
export function isOwned(el: Element | null | undefined): boolean { return !!el && OWNED.has(el); }

/** What a verdict asks of the reader — seven grades, four things to do about them:
 *  `good` the warehouse reproduces it, `bad` the warehouse contradicts it (argue with
 *  the number), `ask` nobody answers to the word (define it), `none` no check was run
 *  (a column the warehouse does not hold, a check that failed) — which is a decision,
 *  not a failure to ground, and must not wear the colour of one. */
export type VerdictFamily = 'good' | 'bad' | 'ask' | 'none';
const FAMILY: Record<string, VerdictFamily> = {
  match: 'good', ambiguous: 'good',
  stale: 'bad', mismatch: 'bad',
  unknown_term: 'ask',
  error: 'none', skipped: 'none',
};
/** A verdict this build has never heard of reads as "no check was run" — never as a match. */
export function verdictFamily(verdict: string): VerdictFamily { return FAMILY[verdict] || 'none'; }

/** The verdict wash colours: green reproduces, red contradicts, amber to define, grey not checked —
 *  the dark set when the number sits on a dark page (#570). */
export function washColors(verdict: string, dark = false): { bg: string; shadow: string; ink: string; edge: string } {
  return (dark ? EXTENSION_VERDICT_COLORS_DARK : EXTENSION_VERDICT_COLORS)[verdictFamily(verdict)];
}

/** The verdict-coloured wash on a number itself. */
export function markStyle(verdict: string, dark = false): string {
  const c = washColors(verdict, dark);
  return `border-radius:4px;padding:0 3px;cursor:pointer;background:${c.bg};box-shadow:inset 0 -1.5px 0 ${c.shadow};`;
}

/** Does this element sit on a dark page? The first ancestor that paints a mostly-opaque background decides; a
 *  page that paints none reads as the browser's light default. Read before we wash anything, so our own
 *  background is never what it measures. */
export function isDarkBackdrop(el: Element | null | undefined): boolean {
  const view = el?.ownerDocument?.defaultView || (typeof window !== 'undefined' ? window : null);
  let next: Element | null = el || null;
  while (next) {
    const n: Element = next;
    next = n.parentElement;
    let bg = '';
    try { bg = view ? view.getComputedStyle(n).backgroundColor : ''; } catch { bg = ''; }
    const m = /rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?/.exec(bg || '');
    if (!m) continue;
    const alpha = m[4] == null ? 1 : m[4].endsWith('%') ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    if (!(alpha >= 0.5)) continue;
    const [r, g, b] = [m[1], m[2], m[3]].map((v) => { const c = parseFloat(v) / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.18;
  }
  return false;
}

/** Take the extension's own chips off the page and heal the text they split, so a re-read sees the page as it was. */
export function clearChips(doc: Document): number {
  const chips = Array.from(doc.querySelectorAll(`.${CHIP_CLASS}`)).filter(isOwned);
  const parents = new Set<Node>();
  for (const el of chips) { if (el.parentNode) parents.add(el.parentNode); el.remove(); }
  // a highlight wraps the number, so healing it means putting the number back where it was, not deleting it —
  // and only OUR marks: a page element that happens to share the class is left untouched
  for (const el of Array.from(doc.querySelectorAll(`.${MARK_CLASS}`))) {
    if (!isOwned(el)) continue;
    const p = el.parentNode;
    if (!p) continue;
    while (el.firstChild) p.insertBefore(el.firstChild, el);
    el.remove();
    parents.add(p);
  }
  parents.forEach((p) => p.normalize());
  clearCellWashes(doc);
  return chips.length;
}

/** Where to put a chip after each grounded inline number: per text node, highest offset first, so an earlier
 *  split never moves a later number's offset. Returns how many chips were placed. */
export function placeInlineChips(inline: CollectedInline[], grounded: { id: string; anchorInline: string; rendered: string | null }[],
  make: (id: string) => Element, mark?: (id: string) => Element): number {
  const byNode = new Map<Text, { offset: number; length: number; id: string; expected: string }[]>();
  for (const g of grounded) {
    const h = inline.find((i) => i.id === g.anchorInline);
    if (!h || !h.node.parentElement) continue;
    const expected = g.rendered || h.rendered || '';
    const list = byNode.get(h.node) || [];
    list.push({ offset: h.offset, length: expected.length, id: g.id, expected });
    byNode.set(h.node, list);
  }
  const dash = (t: string) => t.replace(/[\u2212\u2013\u2014]/g, '-');
  let placed = 0;
  byNode.forEach((list, node) => {
    if (node.parentElement && node.parentElement.namespaceURI !== 'http://www.w3.org/1999/xhtml') return;   // SVG text: an HTML wrapper would hide it
    list.sort((a, b) => b.offset - a.offset);
    for (const item of list) {
      const start = Math.min(item.offset, node.data.length);
      const end = Math.min(item.offset + item.length, node.data.length);
      // the page may have changed under the async round-trip: only mark when the
      // text still at the stored offset is the number that was graded (#195)
      if (item.expected && dash(node.data.slice(start, end)) !== dash(item.expected)) continue;
      const after = node.splitText(end);
      // wash the number by wrapping a SPLIT-OFF fragment — never the page's own node. At offset 0 there is no
      // leading fragment to split, so wrapping would reparent (or empty) the original node and a framework that
      // owns it can throw; there we place the chip alone. Offset > 0 leaves the original node non-empty.
      if (mark && start > 0) {
        const num = node.splitText(start);
        const m = mark(item.id);
        num.parentElement?.insertBefore(m, num);
        m.appendChild(num);
      }
      after.parentElement?.insertBefore(make(item.id), after);
      placed += 1;
    }
  });
  return placed;
}

/** Is `rendered` present in an element as its OWN number token? Reads the element's composed text (so a value
 *  split across markup like <span>$</span><strong>17,516</strong> still counts), whitespace-collapsed and
 *  dash-folded, flanked by non-[digit,comma,dot,sign] / non-[digit,comma,dot] so "2,000" never matches inside
 *  "12,000". This gates the verdict CHIP; washNumberIn (per text node) does the best-effort highlight. */
export function cellShowsNumber(el: Element, rendered: string | null | undefined): boolean {
  if (!rendered) return false;
  const fold = (t: string) => t.replace(/[\u2212\u2013\u2014]/g, '-').replace(/\s+/g, ' ').trim();
  // read the cell the way it was COLLECTED: a value carrying a footnote ("924<sup>1</sup>") was grounded as
  // "924", and a guard still seeing "9241" would refuse to place the chip on a number that did ground
  const hay = fold(textSansEditors(el, 4000, new WeakMap<Element, boolean>(), FOOTNOTES));
  const needle = fold(rendered);
  if (!needle) return false;
  let from = 0;
  let i: number;
  while ((i = hay.indexOf(needle, from)) >= 0) {
    if (!/[\d.,+\u002D]/.test(hay[i - 1] || '') && !/[\d.,]/.test(hay[i + needle.length] || '')) return true;
    from = i + 1;
  }
  return false;
}

export const CELL_WASH_ATTR = 'data-voyager-wash';
// Cells WE washed, with the exact inline styles they had before — by node
// identity, so a page element that merely carries the attribute is never
// touched, and every overwritten property is restored, not blanked.
const WASHED = new WeakMap<Element, { background: string; boxShadow: string; cursor: string; verdict: string; dark: boolean }>();

function _applyCellWash(cell: HTMLElement, verdict: string, dark: boolean): void {
  const c = washColors(verdict, dark);
  cell.style.background = c.bg;
  cell.style.boxShadow = `inset 0 -1.5px 0 ${c.shadow}`;
}

/** Wash a table cell with a verdict-coloured background ON THE CELL — never wrapping, reparenting or moving its
 *  own nodes, so component lifecycles and `td > *` selectors are untouched. The cell's exact prior inline
 *  background AND box-shadow are stashed so clear restores them. A cell holding both a level and its change is
 *  washed once; if any of its claims is stale, stale wins (a mismatch must never read as a match). */
/** Is this exact element a cell WE washed? Identity-backed, so a page element that merely carries the attribute is not. */
export function isWashedCell(el: Element | null | undefined): boolean { return !!el && WASHED.has(el); }

// A cell reads green only if EVERY claim on it is good: the most doubtful claim wins the
// colour, so a cell holding a reproduced level and a contradicted change reads red.
const _FAMILY_RANK: Record<VerdictFamily, number> = { good: 0, none: 1, ask: 2, bad: 3 };
function _rank(verdict: string): number { return _FAMILY_RANK[verdictFamily(verdict)]; }
export function washCell(cell: HTMLElement, verdict: string): void {
  const prior = WASHED.get(cell);
  if (prior) {
    if (_rank(verdict) > _rank(prior.verdict)) { prior.verdict = verdict; _applyCellWash(cell, verdict, prior.dark); }
    return;
  }
  const dark = isDarkBackdrop(cell);                       // measured before our background goes on
  WASHED.set(cell, { background: cell.style.background || '', boxShadow: cell.style.boxShadow || '', cursor: cell.style.cursor || '', verdict, dark });
  cell.setAttribute(CELL_WASH_ATTR, '');
  cell.style.cursor = 'pointer';                           // the washed number is clickable
  _applyCellWash(cell, verdict, dark);
}

/** Restore every cell WE washed to the exact inline styles it had before. */
export function clearCellWashes(doc: Document): void {
  for (const el of Array.from(doc.querySelectorAll(`[${CELL_WASH_ATTR}]`))) {
    const prior = WASHED.get(el);
    if (!prior) continue;                                    // not ours: leave the page's own attribute alone
    const cell = el as HTMLElement;
    cell.style.background = prior.background;
    cell.style.boxShadow = prior.boxShadow;
    cell.style.cursor = prior.cursor;
    cell.removeAttribute(CELL_WASH_ATTR);
    WASHED.delete(el);
  }
}

/**
 * Who sent the open message (#196): the address on the message's own header —
 * Gmail keeps it on the sender element's `email` attribute, Outlook web in
 * the header's title — so a recurring email is one source every day. Null
 * when the page does not say; a mapping is then not offered, never guessed.
 */
export function messageSender(doc: Document, root: Element | null): string | null {
  const scope = messageContainer(root);
  if (!scope) return null;
  const withAttr = Array.from(scope.querySelectorAll('[email]')).find((el) => /@/.test(el.getAttribute('email') || '') && isShown(el));
  if (withAttr) return clean(withAttr.getAttribute('email')).toLowerCase();
  const titled = Array.from(scope.querySelectorAll('[title*="@"], [aria-label*="@"]')).find((el) => isShown(el));
  if (titled) {
    const m = ((titled.getAttribute('title') || titled.getAttribute('aria-label') || '').match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/) || [])[0];
    if (m) return m.toLowerCase();
  }
  return null;
}

/** The element that holds ONE message — its header and its body — so a sender or subject found is this message's, not
 *  another message's in the same thread view. Null when the body is not inside one (identity is then not claimed). */
function messageContainer(root: Element | null): Element | null {
  if (!root) return null;
  const c = root.closest('.adn, .gs, [role="listitem"], [role="document"]');
  return c && c !== root ? c : (root.matches('[role="document"]') ? root : null);
}

/** The open message's subject: a heading inside the message itself, else the conversation's own subject line (Gmail's
 *  `h2.hP`, one per thread — every message in the thread is under it). Null otherwise. */
export function messageSubject(doc: Document, root: Element | null): string | null {
  const scope = messageContainer(root);
  const inside = scope ? (scope.querySelector('h2.hP, [role="heading"], h1, h2') as HTMLElement | null) : null;
  const text = clean(inside?.textContent) || (scope ? clean((doc.querySelector('h2.hP') as HTMLElement | null)?.textContent) : '');
  return text ? text.slice(0, 300) : null;
}

/** Everything the extension sends for a page: kind, title, window, tables, inline numbers — and, on a mail page, the open message's sender and subject. */
/** The text a reader actually sees under `root`, hidden (collapsed quote) subtrees left out — so a period buried
 *  in collapsed history can never override the one on screen. Bounded. */
function visibleText(root: Element, shown: WeakMap<Element, boolean>, cap = 100000): string {
  return textSansEditors(root, cap, shown);   // visible AND not inside an editor
}

export function collectPage(url: string, doc: Document, opts: { inline?: boolean } = {}) {
  const kind = pageKind(url, doc);
  const roots = messageRoots(doc, kind, url);
  const primary = roots.length ? roots[roots.length - 1] : null;   // the open message names the sender & subject
  // a mailbox with no message open: nothing is read, nothing is sent
  const shown = new WeakMap<Element, boolean>();
  const subject = kind === 'email' && primary ? (messageSubject(doc, primary) || clean((doc.querySelector('h2.hP, [role="heading"]') as HTMLElement | null)?.textContent)) : '';
  const sender = kind === 'email' ? messageSender(doc, primary) : null;
  // Collect the OPEN message first (so the caps never omit what the reader is on), then earlier messages
  // NEWEST-first. One reporting window for the thread, read from what is VISIBLE (never collapsed history):
  // the open message's period, else the newest shown message that names one.
  const byRecency = [...roots].reverse();   // newest → oldest (the open message is newest)
  const order = primary ? [primary, ...byRecency.filter((r) => r !== primary)] : byRecency;
  // The subject heading: inside the message container first (most specific), then Gmail's own subject line
  // (`h2.hP`, one per thread) BEFORE any generic heading — a `[role="heading"]` chrome element (a section
  // label, a date divider) can precede the real subject in the DOM, and picking it would miss the window
  // the subject names. Only a visible heading is taken (never a hidden stale one before it), and only a date
  // range in it reaches the window, so an unrelated heading is inert.
  const subjCandidates = [
    ...Array.from(messageContainer(primary)?.querySelectorAll('h2.hP') || []),
    ...Array.from(doc.querySelectorAll('h2.hP')),
    ...Array.from(messageContainer(primary)?.querySelectorAll('[role="heading"], h1, h2') || []),
    ...Array.from(doc.querySelectorAll('[role="heading"], h1')),
  ];
  const subjEl = subjCandidates.find((el) => isShown(el as HTMLElement, shown) && !(el as Element).closest(EDITOR_CSS)) as HTMLElement | undefined;
  const visibleSubject = subjEl ? clean(textSansEditors(subjEl, 300)).slice(0, 300) : '';
  let window: DateWindow | null = null;
  for (const r of byRecency) {
    window = windowHint(`${r === primary ? `${visibleSubject} ` : ''}${textSansEditors(r, 100000, shown)}`);
    if (window) break;
  }
  if (!window && kind !== 'email') window = windowHint(prose(doc.title, 300));   // an email's tab title can lag onto a prior thread; body/subject only
  const tables: CollectedTable[] = [];
  const inline: CollectedInline[] = [];
  if (kind !== 'tableau') {
    // read EVERY shown message — a scorecard in an earlier, still-expanded message is grounded too
    for (const r of order) {
      if (tables.length < MAX_TABLES) for (const t of collectTables(r, MAX_TABLES - tables.length, shown)) tables.push(t);
      if (opts.inline !== false && inline.length < MAX_INLINE) for (const c of collectInline(r, MAX_INLINE - inline.length, shown)) inline.push(c);
    }
    inline.forEach((h, i) => { h.id = `i${i + 1}`; });   // one id space across all messages: no collisions between roots
  }
  return {
    page: { url, kind, title: clean(doc.title).slice(0, 300), window, ...(kind === 'email' ? { sender, subject: messageSubject(doc, primary) } : {}) },
    tables: tables.map((t) => ({ headers: t.headers, rows: t.rows })),
    // the anchor names the collected handle, so the chip can be placed when the verdict comes back
    claims: inline.map(({ id, label, rendered, context, comparison }) => ({ id, label, rendered, context, anchor: { inline: id }, ...(comparison ? { comparison } : {}) })),
    handles: { tables, inline },
  };
}


/**
 * What a perspective can be re-grounded on (#196): every metric (as its
 * qualified id) and every dimension, the core model's first, then the
 * inherited models' — recursively, breadth-first, the shallowest definition
 * of a name winning — the order the query generator binds them. A draft
 * definition (#151) in the model is the caller's OWN (the catalog overlays
 * only published definitions and the caller's drafts), so it is offered,
 * marked `draft`: usable for a preview, never remembered (#197).
 */
export function flattenModel(model: any): { metrics: { qid: string; label: string; draft: boolean; windowable: boolean }[]; dimensions: { name: string; label: string }[] } {
  const metrics: { qid: string; label: string; draft: boolean; windowable: boolean }[] = [];
  const dimensions: { name: string; label: string }[] = [];
  const seenM = new Set<string>();
  const seenD = new Set<string>();
  // the whole inheritance tree, shallowest first — the shallowest definition of a name wins
  const nodes: { node: any; modelName: string | null }[] = [];
  if (model && typeof model === 'object') nodes.push({ node: model, modelName: model?.model || model?.core || null });
  let level: any[] = ((model?.inheritedModels || []) as any[]).filter((n) => n && typeof n === 'object');
  while (level.length) {
    const next: any[] = [];
    for (const node of level) {
      nodes.push({ node, modelName: node.model || node.name || null });
      next.push(...(((node.inheritedModels || []) as any[]).filter((n) => n && typeof n === 'object')));
    }
    level = next;
  }
  // A model is WINDOWABLE when it defines a date_field. A metric on a model without one (a dimension
  // such as `consumer`) cannot be measured over a period — the engine refuses a date range it has
  // nothing to apply to — so a windowed objective on it can never run. Resolved per OWNING model, so a
  // metric an aliased join attributes to another model reads that model's date_field — and an owner
  // the tree does not carry is UNKNOWN, never the containing node's: a metric is not greyed on missing
  // information (the server catalog is the authority and refuses a dateless one at deploy).
  const dated = new Map<string, boolean>();
  for (const { node, modelName } of nodes) if (modelName && !dated.has(modelName)) dated.set(modelName, !!node?.date_field);
  for (const { node, modelName } of nodes) {
    for (const m of (node?.metrics || []) as any[]) {
      if (!m?.name || seenM.has(m.name)) continue;
      seenM.add(m.name);
      const owner = m.model || modelName || '';
      metrics.push({ qid: `${owner}.${m.name}`, label: m.display_name || m.name,
        draft: !!(m._definition && typeof m._definition === 'object' && m._definition.status === 'draft'),
        windowable: dated.has(owner) ? !!dated.get(owner) : true });
    }
    for (const d of (node?.dimensions || []) as any[]) {
      if (!d?.name || seenD.has(d.name)) continue;
      seenD.add(d.name);
      dimensions.push({ name: d.name, label: d.display_name || d.name });
    }
  }
  return { metrics, dimensions };
}
